<template>
    <div class="staff_edit_wrap" v-if="isShow" ref="frame" :style="styleString" v-clickoutside:action_Click_Outside="{}">
        <div class="staff_input_wrap">
            <SelectedItem v-for="(employee, index) in employees" :key="index" :employee="employee" :index="index" @unselectEmployee="unselectEmployee" />
            <input ref="input" type="text" :placeholder="$t('work_schedule.calendar.placeholder_staff_name')" v-model="keyword" @keydown.enter="action_InputEnter" />
        </div>
        <SearchResult :keyword="keyword" :results="refinedResult" :focusedItem="focusedItem" @selectEmployee="selectEmployee" />
    </div>
</template>

<script>
import { deepCopy } from '@/util/deep-copy'
import SelectedItem from './CalendarSearchSelectedItem.vue'
import SearchResult from './CalendarSearchResult.vue'

import { workerState, hideSearchLayer } from '../../../../../worker-state'
import { useQueryClient } from '@tanstack/vue-query'
import { isApiErrorWithCode } from '@/apis/instances/check-api-response'
import { v2EmployeeFindQueryOptions } from '@/composables/queries/v2employee/use-v2-employee-find-query'

const moment = require('@/util/MPMoment')

export default {
    setup() {
        const queryClient = useQueryClient()
        return { queryClient }
    },
    components: { SelectedItem, SearchResult },
    props: {
        scroll: HTMLElement,
        keyEventController: Object
    },
    data() {
        return {
            isLoading: false,

            worker: workerState,

            keyword: '',

            searchResult: [],
            focusedItem: undefined,

            resizeObserver: undefined,
            contentHeight: 0
        }
    },
    watch: {
        isShow: {
            handler: function (newValue) {
                if (newValue == false) {
                    this.unregistKeyEventHandler()
                    this.unregistContentHeightHandler()
                    return
                }
                this.keyword = ''
                this.searchResult = []
                this.registKeyEventHandler()
                this.$nextTick(() => {
                    this.registContentHeightHandler()
                    this.loadEmployee()
                    let el = this.$refs.input
                    if (el != undefined) {
                        el.focus()
                    }
                })
            }
        },
        employees: {
            deep: true,
            handler: function () {
                // 이 컴포넌트는 항상 마운트되어 있어, 닫힌 상태에서도 포커스 이동
                // (focusedRecord 변경)마다 검색 API 가 나가던 것을 차단한다.
                // 열릴 때는 isShow 워처가 loadEmployee 를 호출하므로 최신성은 유지된다.
                if (this.isShow == false) {
                    return
                }
                this.loadEmployee()
            }
        },
        keyword: {
            handler: function () {
                this.loadEmployee()
            }
        },
        focusedRecord: {
            handler: function (newValue, oldValue) {
                this.keyword = ''
                if (newValue != oldValue) {
                    this.$nextTick(() => {
                        this.$forceUpdate()
                        let el = this.$refs.input
                        if (el != undefined) {
                            el.focus()
                        }
                    })
                }
            }
        }
    },
    computed: {
        isShow: function () {
            return this.worker.searchLayer_Key != undefined && this.focusedElement != undefined
        },
        focusedRecord: function () {
            return this.worker.focusedRecord
        },
        employees: function () {
            return this.focusedRecord?.employees.filter((e) => e.isRemoved == false) ?? []
        },
        focusedElement: function () {
            return this.worker.focusedElement
        },
        height: function () {
            return this.contentHeight ?? 0
        },
        styleString: function () {
            let newElement = this.focusedElement
            if (!newElement) return ''

            let wrapperElement = this.findScrollableParent(newElement)
            let width = newElement.offsetWidth ? newElement.offsetWidth - 18 : 0
            let top = 0,
                left = 0,
                element = newElement

            while (element && this.scroll?.contains(element)) {
                top += element.offsetTop || 0
                element = element.offsetParent
            }
            top = top ? top + 10 : 0

            element = newElement
            while (element && this.scroll?.contains(element)) {
                left += element.offsetLeft || 0
                element = element.offsetParent
            }
            left = left ? left + 10 : 0

            let scrollTop = wrapperElement?.scrollTop || 0
            top -= scrollTop

            const scheduleScroll = document.querySelector('.schedule_scroll')?.scrollTop
            const rect = document.querySelector('.schedule_scroll')?.getBoundingClientRect()

            if (top - scheduleScroll + this.contentHeight > rect?.height ?? 0) {
                top -= this.contentHeight
            } else {
                top += 10
            }

            return `width: ${width}px; left: ${left}px; top: ${top}px;`
        },

        selectedRecord: function () {
            return this.worker.focusedRecord
        },
        currentDate: function () {
            return moment(this.selectedRecord.date)
        },
        currentDateRecords: function () {
            let dateKey = moment(this.currentDate).format()
            return this.worker.modifiedRecords[dateKey]?.modifiedRecords ?? []
        },
        employeesArray: function () {
            return this.currentDateRecords.map((r) => r.employees)
        },
        includedEmployeeIDs: function () {
            return this.employeesArray.reduce((acc, array) => {
                array.forEach((item) => {
                    if (!acc.includes(item._id)) {
                        acc.push(item._id)
                    }
                })
                return acc
            }, [])
        },
        refinedResult: function () {
            return this.searchResult.filter((employee) => !this.includedEmployeeIDs.includes(employee._id))
        }
    },
    destroyed() {
        this.unregistKeyEventHandler()
    },
    methods: {
        // MARK: - Actions
        action_InputEnter() {
            this.loadEmployee()
        },
        action_Click_Outside() {
            if (this.AlertController.currentAlert != undefined) { return }
            this.$nextTick(() => {
                if (this.isShow == false) {
                    return
                }
                hideSearchLayer()
            })
        },

        findScrollableParent(el) {
            while (el) {
                const overflowY = window.getComputedStyle(el).overflowY
                if (overflowY === 'scroll' || overflowY === 'auto') {
                    return el
                }
                el = el.parentElement
            }
            return document.body // Fallback to body if no scrollable parent is found
        },
        // MARK: - Network
        async loadEmployee() {
            let hospitalID = this.hospital.selectedHospital._id
            let model = {
                hospitalID: hospitalID,
                isRemoved: false
            }
            if (this.keyword != undefined && this.keyword != '') {
                model.keyword = this.keyword
            }

            this.isLoading = true
            this.hasSearch = true
            try {
                this.searchResult = await this.queryClient.fetchQuery(v2EmployeeFindQueryOptions(model))
            } catch (err) {
                // 기존 동작 미러링: 봉투 code != 0 이면 로그만 남기고 결과 갱신을 건너뛴다
                if (!isApiErrorWithCode(err)) throw err
                console.log(err)
                return
            } finally {
                this.isLoading = false
            }

            this.refineSearchResult()
        },
        refineSearchResult() {
            let array = deepCopy(this.searchResult)
            let refinedArray = []
            let ids = this.employees.map((v) => v._id)
            for (let i = 0; i < array.length; i++) {
                let e = array[i]
                if (ids.includes(e._id)) {
                    continue
                }
                refinedArray.push(e)
            }
            this.searchResult = refinedArray
        },

        // MARK: - Emit
        selectEmployee(employee) {
            this.worker.f_SelectEmployee(employee)
        },
        unselectEmployee(employee, index) {
            this.worker.f_UnselectEmployee(employee, index)
        },

        // MARK: - Event Handler
        registContentHeightHandler() {
            this.resizeObserver = new ResizeObserver((entries) => {
                for (let entry of entries) {
                    if (entry.target === this.$refs.frame) {
                        this.handleContentHeight()
                    }
                }
            })
            this.resizeObserver.observe(this.$refs.frame)
        },
        unregistContentHeightHandler() {
            if (this.resizeObserver?.unobserve != undefined) {
                this.resizeObserver.unobserve(this.$refs.frame)
            }
            if (this.resizeObserver?.disconnect != undefined) {
                this.resizeObserver.disconnect()
            }
            this.resizeObserver = undefined
        },
        handleContentHeight() {
            this.contentHeight = this.$refs.frame.offsetHeight
        },

        registKeyEventHandler() {
            let router = this.keyEventController.getNextRouteObject('keydown')
            router.target = this._uid
            router.groupKey = 'keydown_search'
            router.policy = this.keyEventController.Enum.RoutingPolicy.Go
            router.handler = this.handleKeyDownEvent
            this.keyEventController.registRouter(router)
        },
        unregistKeyEventHandler() {
            this.keyEventController?.unregistRouter(this._uid)
        },
        handleKeyDownEvent(e) {
            let key = e.key
            let currentItemIndex = -1
            if (this.refinedResult.length != 0 && this.focusedItem != undefined) {
                currentItemIndex = this.refinedResult.indexOf(this.focusedItem)
            }

            switch (key) {
                case 'ArrowUp':
                    if (this.refinedResult.length != 0) {
                        currentItemIndex -= 1
                    }
                    e.preventDefault()
                    break
                case 'ArrowDown':
                    if (this.refinedResult.length != 0) {
                        currentItemIndex += 1
                    }
                    e.preventDefault()
                    break
                case 'Enter':
                    if (this.focusedItem != undefined) {
                        e.preventDefault()
                        this.keyword = ''
                        this.selectEmployee(this.focusedItem)
                        this.focusedItem = undefined
                        let el = this.$refs.input
                        if (el != undefined) {
                            el.focus()
                        }
                    } else {
                        return
                    }
                    return
                case 'Backspace':
                    {
                        if (this.keyword == '' && this.employees.length > 0) {
                            let lastIndex = this.employees.length - 1
                            let employee = this.employees[lastIndex]
                            this.unselectEmployee(employee, lastIndex)
                            return
                        }
                    }
                    break
                case 'Escape':
                    this.worker.searchLayer_Key = undefined
                    e.preventDefault()
                    break
            }
            if (currentItemIndex < 0) {
                let el = this.$refs.input
                this.focusedItem = undefined
                if (el != undefined) {
                    el.focus()
                }
                return
            } else {
                let el = this.$refs.input
                if (el != undefined) {
                    el.blur()
                }
            }
            if (currentItemIndex >= this.refinedResult.length) {
                currentItemIndex = this.refinedResult.length - 1
            }

            this.focusedItem = this.refinedResult[currentItemIndex]
        }
    }
}
</script>
